<template>
  <div>
    <applications-list-add-new
      :is-add-new-user-sidebar-active.sync="isAddNewUserSidebarActive"
      :plan-options="PLAN_OPTIONS"
      @refetch-data="refetchData"
      @add-application="addApplication"
    />
    <print-list
      :list="printList"
      :is-print-list-sidebar-active.sync="isPrintListSidebarActive"
      @remove="removeFromPrintList"
      @exportToCsv="exportToCsv"
    />

    <!-- Dashboard Statistics -->
    <applications-statistics
      v-if="this.$store.state.app.appConfig.type === 'hw-admin'"
      :group-filter.sync="groupFilter"
      @fetch-statistics="fetchStatistics"
    />

    <application-steps
      v-if="this.$store.state.app.appConfig.type === 'hw-admin'"
      :step-filter.sync="stepFilter"
      :badge-application-request="countApplicationRequest"
    />

    <!-- Filters -->
    <applications-list-filters
      v-if="this.$store.state.app.appConfig.type === 'hw-admin'"
      :role-filter.sync="roleFilter"
      :plan-filter.sync="planFilter"
      :status-filter.sync="statusFilter"
      :state-filter.sync="stateFilter"
      :city-filter.sync="cityFilter"
      :seeking-filter.sync="seekingFilter"
      :seeking-start-filter.sync="seekingStartFilter"
      :seeking-end-filter.sync="seekingEndFilter"
      :profession-filter.sync="professionFilter"
      :step-filter.sync="stepFilter"
      :oiwa-filter.sync="oiwaFilter"
      :age-filter.sync="ageFilter"
      :residency-filter.sync="residencyFilter"
      :plan-options="PLAN_OPTIONS"
      :status-options="STATUS_OPTIONS"
      :state-options="STATE_OPTIONS"
      :profession-options="professionOptions"
      :step-options="STEP_OPTIONS"
      :oiwa-options="OIWA_OPTIONS"
      :seeking-options="SEEKING_OPTIONS"
      :age-options="AGE_OPTIONS"
      :residency-options="RESIDENCY_OPTIONS"
    />

    <!-- Table Container Card -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('application.addUserModal.placeholders.search')"
                :debounce="INPUT_DEBOUNCE"
              />
              <b-button v-if="appConfig.type === 'hw-admin'" :variant="COLOR_VARIANTS.PRIMARY" @click="isAddNewUserSidebarActive = true">
                <span class="text-nowrap">{{ $t('application.addUserModal.btns.add') }}</span>
              </b-button>
              <b-button
                v-if="appConfig.type === 'hw-admin'"
                :variant="COLOR_VARIANTS.SECONDARY"
                style="margin-left: 10px"
                @click="() => isPrintListSidebarActive = true"
              >
                <span v-if="printList.length < 1" class="text-nowrap">{{ $t('application.btn.create_list') }}</span>
                <!-- show badge with print list count -->
                <b-badge
                  v-else
                  variant="light"
                  class="text-nowrap"
                  style="cursor: pointer; color:black"
                  @click="() => isPrintListSidebarActive = true"
                >
                  Listan koko: {{ printList.length }}
                </b-badge>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refUserListTable"
        class="position-relative"
        :items="fetchApplications"
        responsive
        :fields="this.$store.state.app.appConfig.type === 'hw-admin' ? hoiwaColumns : oiwaColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no_matching_records')"
        :sort-desc.sync="isSortDirDesc"
        fixed
        :hover="isPrintListSidebarActive"
        @row-clicked="rowClicked"
      >
        <!-- Style for columns -->
        <template #table-colgroup="scope">
          <col v-for="field in scope.fields" :key="field.key" :style="{ ...field.style }" />
        </template>

        <!-- Column: Issued Date -->
        <template #cell(created)="data">
          <span class="font-weight-bold">
            {{ data.value.toDate().toLocaleDateString('fi') }}
          </span>
        </template>

        <!-- Column: Seeking -->
        <!-- eslint-disable-next-line -->
        <template #cell(application.seeking)="{ value }">
          <b-avatar-group v-if="value && value.length > 0" size="35px">
            <b-avatar
              v-if="value.includes('gig')"
              v-b-tooltip.hover.bottom
              title="Keikka"
              text="K"
              :variant="COLOR_VARIANTS.SECONDARY"
            />
            <b-avatar
              v-if="value.includes('long-term')"
              v-b-tooltip.hover.bottom
              title="Pitkäaikainen"
              text="P"
              :variant="COLOR_VARIANTS.PRIMARY"
            />
            <b-avatar
              v-if="value.includes('periodic')"
              v-b-tooltip.hover.bottom
              title="Kausiluonteista"
              text="KA"
              :variant="COLOR_VARIANTS.INFO"
            />
          </b-avatar-group>
        </template>

        <!-- Column: Seeking Start -->
        <!-- eslint-disable-next-line -->
        <template #cell(application.start)="{ value }">
          <span>{{ value ? getSeekingTime(value.toDate()) : '' }}</span>
        </template>

        <!-- Column: Seeking End -->
        <!-- eslint-disable-next-line -->
        <template #cell(application.end)="{ value }">
          <span>{{ value ? getSeekingTime(value.toDate()) : '' }}</span>
        </template>

        <!-- Column: User -->
        <template #cell(user)="data">
          <b-media v-if="!isPrintListSidebarActive && printList.length < 1" vertical-align="center">
            <b-link
              :to="{ name: 'apps-applications-view', params: { id: data.item.id } }"
              class="font-weight-bold d-block text-nowrap"
              style="text-transform: capitalize"
            >
              {{ data.item.fullName }}
            </b-link>
          </b-media>
          <b-media v-else vertical-align="center">
            <b-link
              class="font-weight-bold d-block text-nowrap"
              style="text-transform: capitalize"
              @click="() => rowClicked(data.item)"
            >
              {{ data.item.fullName }}
            </b-link>
          </b-media>
        </template>

        <!-- Column: countUpdates -->
        <template #cell(countUpdates)="data">
          <b-badge
            v-if="
              data.item.applicationNotifications &&
                data.item.applicationNotifications.filter(
                  item => item === 'document_request_answer'
                ).length > 0
            "
            pill
            :variant="COLOR_VARIANTS.LIGHT_WARNING"
          >
            {{
              data.item.applicationNotifications.filter(item => item === 'document_request_answer')
                .length
            }}
          </b-badge>
        </template>

        <!-- Column: Unresolved comments -->
        <template #cell(unresolvedComments)="data">
          <b-avatar
            v-if="getApplicantComment(data.item) > 0"
            v-b-tooltip.hover
            :title="$t('application.commentBadgeTitle')"
            :badge="getApplicantComment(data.item).toString()"
            :variant="COLOR_VARIANTS.SECONDARY"
          >
            <feather-icon
              icon="UserCheckIcon"
            />
          </b-avatar>
        </template>

        <!-- Column: Last update -->
        <template #cell(applicationUpdates)="data">
          <span v-if="data.item.applicationUpdates" style="flex: 1">
            <b-avatar
              v-b-tooltip.hover
              :title="getUpdateMessage(data.item.applicationUpdates)"
              :badge="data.item.applicationUpdates.length.toString()"
              :variant="
                `light-${getVariant(
                  data.item.applicationUpdates[data.item.applicationUpdates.length - 1].state
                )}`
              "
              class="mr-1"
            >
              <feather-icon
                :icon="
                  getIcon(
                    data.item.applicationUpdates[data.item.applicationUpdates.length - 1].state
                  )
                "
              />
            </b-avatar>
            <b-avatar
              v-b-tooltip.hover
              :variant="COLOR_VARIANTS.LIGHT_SUCCESS"
              :title="getProcesserName(data.item.applicationUpdates)"
              :text="getProcesserInitials(data.item.applicationUpdates)"
              class="marg-right-5"
            />
            <span class="font-weight-bold">
              {{ getUpdateDate(data.item.applicationUpdates) }}
            </span>
          </span>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge
            pill
            :variant="resolveUserStatusVariant(data.item.status)"
            class="text-capitalize"
          >
            {{ data.item.status }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret :right="$store.state.hwUiLayout.isRTL">
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
            </template>
            <b-dropdown-item :to="{ name: 'apps-applications-view', params: { id: data.item.id } }">
              <feather-icon icon="FileTextIcon" />
              <span class="align-middle ml-50">Details</span>
            </b-dropdown-item>
            <b-dropdown-item v-b-modal.modal-danger @click="deleteUser(data.item.id)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <div class="mx-2 mb-2 d-flex justify-content-between align-items-center">
        <!---<div class="d-flex align-items-center">
          <label class="text-lg">Per page</label>
          <div class="ml-1">
            <b-form-select :value="perPage" :options="perPageOptions" @change="changePerPage" />
          </div>
        </div>-->
        <ul role="menubar" class="pagination mr-5 mb-0 b-pagination pagination-lg">
          <li
            role="presentation"
            :class="['page-item', currentPage > 1 ? '' : 'disabled']"
            @click="changePage('prev')"
          >
            <button role="menuitem" type="button" class="page-link">
              <span class="text-primary">
                Prev
              </span>
            </button>
          </li>
          <li role="presentation" class="page-item active">
            <button role="menuitemradio" type="button" class="page-link">
              {{ currentPage }}
            </button>
          </li>
          <li
            role="presentation"
            :class="['page-item', canLoadMore ? '' : 'disabled']"
            @click="changePage('next')"
          >
            <button role="menuitem" type="button" class="page-link">
              <span class="text-primary">
                Next
              </span>
            </button>
          </li>
        </ul>
      </div>
    </b-card>
    <b-modal
      id="modal-danger"
      ok-only
      ok-variant="danger"
      ok-title="Delete"
      modal-class="modal-danger"
      centered
      title="Deleting User"
      @ok="confirmDelete"
    >
      <b-card-text>
        You are deleting an application, this operation cannot be done.
      </b-card-text>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardText,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BMedia,
  BLink,
  BAvatarGroup,
  BAvatar,
  BBadge,
  BDropdown,
  BDropdownItem,
  BModal,
  VBModal,
  VBTooltip,
  BButton,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import store from '@/shared/store'
import { ref, computed } from '@vue/composition-api'
// eslint-disable-next-line import/no-cycle
import { avatarText } from '@hw-ui/utils/filter'
import * as moment from 'moment'
import 'moment-timezone'

import { COLOR_VARIANTS, INPUT_DEBOUNCE, STATE_OPTIONS, STEP_OPTIONS, OIWA_OPTIONS, PLAN_OPTIONS, STATUS_OPTIONS, SEEKING_OPTIONS, AGE_OPTIONS, RESIDENCY_OPTIONS } from '@/shared/constants'

import ApplicationsListFilters from './ApplicationsListFilters.vue'
import useApplicationsList from './useApplicationsList'
import ApplicationsListAddNew from './ApplicationsListAddNew.vue'
import ApplicationsStatistics from './ApplicationsStatistics.vue'
import ApplicationSteps from './ApplicationSteps.vue'
import PrintList from './PrintList.vue'

export default {
  components: {
    ApplicationsStatistics,
    ApplicationsListFilters,
    ApplicationsListAddNew,
    PrintList,
    BModal,
    BAvatarGroup,
    BAvatar,
    BCard,
    BCardText,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BMedia,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BButton,
    ApplicationSteps,
  },
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      INPUT_DEBOUNCE,
      STATE_OPTIONS,
      STEP_OPTIONS,
      OIWA_OPTIONS,
      PLAN_OPTIONS,
      STATUS_OPTIONS,
      SEEKING_OPTIONS,
      AGE_OPTIONS,
      COLOR_VARIANTS,
      RESIDENCY_OPTIONS,
      printList: [],
    }
  },
  computed: {
    appConfig() {
      return this.$store.state.app.appConfig || {}
    },
  },
  methods: {
    // onLoadMore() {
    //   this.currentPage += 1
    //   this.loadMore = true
    // },
    removeFromPrintList(id) {
      const index = this.printList.findIndex(item => item.id === id)
      this.printList.splice(index, 1)
    },
    rowClicked(item) {
      if(!this.isPrintListSidebarActive && this.printList.length === 0) {
        return
      }
      // Check if is already in list, if not add object to list
      const index = this.printList.findIndex(printItem => printItem.id === item.id)
      if(index === -1) {
        this.printList.push(item)
        this.$bvToast.toast(`Added ${item.fullName} to print list`, {
          title: 'Success',
          variant: 'info',
          solid: false,
        })
      }
    },
    async exportToCsv() {
      /** Export data to csv */
      const data = this.printList.map(item => {
        const { fullName, email, mobile, profession, love, createdAt} = item
        return {
          applied: moment(createdAt).format('MM.DD.YYYY'),
          name: fullName,
          rooli: profession,
          luvallinen: love ? 'Kyllä' : 'Ei',
          puhelin: mobile,
          soitettu: ' ',
          muistiinpanot: ' ',
          sitoutuuko: ' ',
          email,
          link: `https://hoiwa-app.web.app/apps/hr/applications/view/${item.id}`,
        }
      })
      const replacer = (key, value) => (value === null ? '' : value) // specify how you want to handle null values here
      const header = Object.keys(data[0])
      let csv = data.map(row => header.map(fieldName => JSON.stringify(row[fieldName], replacer)).join(','))
      csv.unshift(header.join(','))
      csv = csv.join('\r\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', 'puhelu_lista_hakijat.csv')
      link.click()
    },
    getApplicantComment(application) {
      if (!application?.applicationNotifications?.length) {
        return 0
      }
      return application?.applicationNotifications?.filter(item => item === 'user_application_comment').length
    },
    toMinutes(time) {
      const minutes = Math.floor(time / 60)
      const seconds = time - minutes * 60
      return `${minutes}m ${seconds}s`
    },
    getUpdateMessage(applicationUpdates) {
      const index = applicationUpdates.length - 1
      const { content } = applicationUpdates[index]
      const message = content.message ? content.message : 'No Message'
      return message
    },
    getUpdateDate(applicationUpdates) {
      const index = applicationUpdates.length - 1
      const { date } = applicationUpdates[index]
      if (date) {
        return moment(date.toDate()).tz('Europe/Helsinki').format('DD.MM.YYYY')
      }
      return ''
    },
    getProcesserName(applicationUpdates) {
      const index = applicationUpdates.length - 1
      const { processer } = applicationUpdates[index]
      return `${processer.firstName} ${processer.lastName}`
    },
    getProcesserInitials(applicationUpdates) {
      const index = applicationUpdates.length - 1
      const { processer } = applicationUpdates[index]
      return `${processer.firstName?.charAt(0)} ${processer.lastName?.charAt(0)}`
    },
    getVariant(state) {
      const resultByState = {
        accepted: 'success',
        confirm_valvira: 'primary',
        no_valvira: 'danger',
        rejected: 'danger',
        schedule: 'warning',
        no_answer: 'secondary',
        send_interview: 'info',
        confirm_interview: 'primary',
        request_documents: 'warning',
        submit_interview: 'primary',
        confirmed_documents: 'primary',
        send_sms: 'info',
        send_message: 'info',
        contract_sign_request: 'info',
        contract_signed: 'success',
      }
      return resultByState[state] || 'info'
    },
    getIcon(state) {
      let result = ''
      switch (state) {
        case 'accepted':
          result = 'ThumbsUpIcon'
          break
        case 'confirm_valvira':
          result = 'UserCheckIcon'
          break
        case 'no_valvira':
          result = 'UserMinusIcon'
          break
        case 'rejected':
          result = 'ThumbsDownIcon'
          break
        case 'schedule':
          result = 'CalendarIcon'
          break
        case 'no_answer':
          result = 'PhoneOffIcon'
          break
        case 'send_interview':
          result = 'SendIcon'
          break
        case 'submit_interview':
          result = 'MessageSquareIcon'
          break
        case 'request_documents':
          result = 'FileTextIcon'
          break
        case 'confirmed_documents':
          result = 'MailIcon'
          break
        case 'confirm_interview':
          result = 'CheckSquareIcon'
          break
        case 'send_sms':
          result = 'MessageCircleIcon'
          break
        case 'send_message':
          result = 'InboxIcon'
          break
        case 'contract_sign_request':
          result = 'ShareIcon'
          break
        case 'contract_signed':
          result = 'Edit3Icon'
          break
        default:
          result = 'ArrowRightIcon'
      }
      return result
    },
    getState(state) {
      let result = ''
      switch (state) {
        case 'accepted':
          result = 'Hyväksytty'
          break
        case 'confirm_valvira':
          result = this.$t('application.steps.valviraConfirmed')
          break
        case 'rejected':
          result = 'Hylätty'
          break
        case 'schedule':
          result = this.$t('application.steps.interviewScheduled')
          break
        case 'no_answer':
          result = this.$t('application.steps.noAnswer')
          break
        case 'send_interview':
          result = this.$t('application.steps.interviewSent')
          break
        case 'submit_interview':
          result = this.$t('application.steps.interviewSubmitted')
          break
        case 'request_documents':
          result = this.$t('application.steps.requestDocuments')
          break
        case 'confirmed_documents':
          result = this.$t('application.steps.confirmedDocuments')
          break
        case 'confirm_interview':
          result = this.$t('application.steps.interviewConfirmed')
          break
        case 'send_sms':
          result = 'Send SMS'
          break
        case 'send_message':
          result = 'Sposti lähetetty'
          break
        case 'contract_sign_request':
          result = this.$t('application.steps.contractSent')
          break
        case 'contract_signed':
          result = 'Sopimus allekirjoitettu'
          break
        default:
          result = this.$t('application.steps.applied')
      }
      return result
    },
    getSeekingTime(date) {
      if (date) {
        return moment(date)
          .tz('Europe/Helsinki')
          .format('DD.MM.YYYY')
      }
      return ''
    },
  },
  setup() {

    const isAddNewUserSidebarActive = ref(false)
    const isPrintListSidebarActive = ref(false)

    const professionOptions = computed(() => {
      const roles = store.state.appAccount.roles.map(role => role.label)
      return roles
    })

    const {
      fetchApplications,
      deleteUser,
      confirmDelete,
      hoiwaColumns,
      oiwaColumns,
      perPage,
      currentPage,
      totalUsers,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,
      // loadMore,
      canLoadMore,
      countApplicationRequest,

      // UI
      resolveUserRoleVariant,
      resolveUserRoleIcon,
      resolveUserStatusVariant,

      // Extra Filters
      roleFilter,
      planFilter,
      statusFilter,
      cityFilter,
      stateFilter,
      groupFilter,
      professionFilter,
      stepFilter,
      oiwaFilter,
      seekingFilter,
      seekingStartFilter,
      seekingEndFilter,
      ageFilter,
      residencyFilter,

      // fetchStatistics
      fetchStatistics,
      // fetchApplicationRequests
      fetchApplicationRequests,
      changePage,
      changePerPage,
      totalRows,
      addApplication,
    } = useApplicationsList()

    fetchApplicationRequests()

    return {
      // Sidebar
      isAddNewUserSidebarActive,
      isPrintListSidebarActive,

      fetchApplications,
      deleteUser,
      confirmDelete,
      hoiwaColumns,
      oiwaColumns,
      perPage,
      currentPage,
      totalUsers,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,
      // loadMore,
      canLoadMore,
      countApplicationRequest,

      // Filter
      avatarText,

      // UI
      resolveUserRoleVariant,
      resolveUserRoleIcon,
      resolveUserStatusVariant,

      // Role options
      professionOptions,

      // Extra Filters
      roleFilter,
      planFilter,
      statusFilter,
      cityFilter,
      stateFilter,
      groupFilter,
      professionFilter,
      stepFilter,
      oiwaFilter,
      seekingFilter,
      seekingStartFilter,
      seekingEndFilter,
      ageFilter,
      residencyFilter,

      // fetchStatistics
      fetchStatistics,
      changePage,
      changePerPage,
      totalRows,
      addApplication,
    }
  },
}
</script>

<style lang="scss">
@import '@hw-ui/scss/vue/libs/vue-select.scss';
</style>
